import {useCallback, useEffect, useRef, useState} from 'react';
import timeElapse from '../../../utils/time/timeElapse';

const timerRefreshRateSeconds = 1;

export interface UseQuizTimedReturn {
  /** total time remaining in seconds. 0 means time up or disabled */
  timeRemaining: number;
  stopTimer: () => void;
}

export default function useSessionTimed(
  /** the start time (ISO string or Date) */
  startedAt?: string | Date,
  /** the duration in seconds before time is up */
  durationSeconds?: number,
  /** the callback when time is up */
  onTimeUp?: () => void,
): UseQuizTimedReturn {
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const intervalTimerRef = useRef<NodeJS.Timeout | null>(null);

  const stopTimer = useCallback(() => {
    if (intervalTimerRef.current) {
      clearTimeout(intervalTimerRef.current);
      intervalTimerRef.current = null;
    }
  }, []);

  // Function to start or reset timer
  useEffect(() => {
    if (durationSeconds === undefined || durationSeconds < 1 || !startedAt) {
      stopTimer();
      setTimeRemaining(0);
      return stopTimer;
    }

    // Start the timer
    const tick = () => {
      const elapsed = timeElapse(startedAt);
      const remainingTime = Math.max(0, durationSeconds - elapsed);

      setTimeRemaining(remainingTime);

      // Check if time is up
      if (elapsed >= durationSeconds) {
        onTimeUp?.();
        return;
      }

      // Schedule next tick
      intervalTimerRef.current = setTimeout(tick, Math.min(remainingTime, timerRefreshRateSeconds) * 1000);
    };

    tick();

    // Cleanup function
    return stopTimer;
    // avoid onTimeUp from dependency array to prevent multiple calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationSeconds, startedAt, stopTimer]);

  // Cleanup on unmount
  useEffect(() => stopTimer, [stopTimer]);

  return {
    timeRemaining,
    stopTimer,
  };
}
